import { useMemo, type ReactNode } from "react";

import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { useLoadNext } from "app/hooks";

import { GroupedUsersGrid } from "./GroupedUsersGrid";

import type { RoleUsersGrouping_query$key } from "./__generated__/RoleUsersGrouping_query.graphql";
import type { RoleUsersGroupingQuery } from "./__generated__/RoleUsersGroupingQuery.graphql";
import type { RoleUsersGroupingRefetchQuery } from "./__generated__/RoleUsersGroupingRefetchQuery.graphql";
import type { BucketRow } from "./GroupedUsersGrid";
import type { RetainedSearch } from "./userGrouping";

/** Roles loaded per page as the bucket list scrolls. */
const ROLE_PAGE = 25;

interface Props extends RetainedSearch {
  /** The group-by dropdown, for the grid toolbar. */
  toolbarActions?: ReactNode;
}

/**
 * Lists roles, loading more as the list scrolls; expanding one shows the users
 * who hold it.
 */
export function RoleUsersGrouping({
  toolbarActions,
  initialSearchTerm,
  onSearchTermChange,
}: Props) {
  const queryData = useLazyLoadQuery<RoleUsersGroupingQuery>(
    graphql`
      query RoleUsersGroupingQuery($first: Int) {
        ...RoleUsersGrouping_query @arguments(first: $first)
      }
    `,
    { first: ROLE_PAGE },
  );

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    RoleUsersGroupingRefetchQuery,
    RoleUsersGrouping_query$key
  >(
    graphql`
      fragment RoleUsersGrouping_query on Query
      @argumentDefinitions(first: { type: "Int" }, after: { type: "String" })
      @refetchable(queryName: "RoleUsersGroupingRefetchQuery") {
        roles(first: $first, after: $after)
          @connection(key: "RoleUsersGrouping_roles") {
          edges {
            node {
              id
              name
              system
            }
          }
          pageInfo {
            total
          }
        }
      }
    `,
    queryData,
  );

  const rows = useMemo<BucketRow[]>(
    () =>
      (data.roles.edges ?? [])
        .map((edge) => edge.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({
          id: node.id,
          label: node.name,
          // Roles have no dedicated details page.
          to: null,
          memberScope: { kind: "role" as const, roleName: node.name },
          detail: node.system ? "System role" : null,
        })),
    [data.roles.edges],
  );

  const loadMore = useLoadNext(hasNext, loadNext, ROLE_PAGE);

  return (
    <GroupedUsersGrid
      bucketTotal={data.roles.pageInfo.total ?? null}
      buckets={rows}
      initialSearchTerm={initialSearchTerm}
      loading={isLoadingNext}
      onLoadMore={loadMore}
      onSearchTermChange={onSearchTermChange}
      searchKind="role"
      tableId="grouped-users-roles"
      toolbarActions={toolbarActions}
      unit="role"
    />
  );
}
